const ACCENT = '#E00035';
const ACCENT_HOVER = '#c0002d';

const COMMON_VARIABLES = {
  colorPrimary: ACCENT,
  colorPrimaryForeground: '#ffffff' as const,
  colorDanger: '#f43f5e',
  colorSuccess: '#30d158',
  colorWarning: '#f59e0b',
  colorRing: ACCENT,
  fontFamily: 'Inter, system-ui, sans-serif' as const,
  fontFamilyButtons: 'Inter, system-ui, sans-serif' as const,
  fontSize: '0.9375rem',
  fontWeight: { normal: 400, medium: 500, semibold: 600, bold: 700 } as const,
  borderRadius: '0.75rem',
  spacing: '1rem',
};

function makeAppearance(isDark: boolean) {
  const card        = isDark ? '#1a1a1a' : '#ffffff';
  const inputBg     = isDark ? '#222222' : '#f5f5f7';
  const hover       = isDark ? '#222222' : '#f5f5f7';
  const border      = isDark ? '#272727' : '#ececee';
  const borderHi    = isDark ? '#333333' : '#d4d4d8';
  const textPrimary = isDark ? '#f1f1f1' : '#09090b';
  const textSecond  = isDark ? '#999999' : '#52525b';

  return {
    variables: {
      ...COMMON_VARIABLES,
      colorNeutral:       isDark ? '#f1f1f1' : '#09090b',
      colorForeground:    textPrimary,
      colorMuted:         hover,
      colorMutedForeground: textSecond,
      colorBackground:    card,
      colorInput:         inputBg,
      colorInputForeground: textPrimary,
      colorBorder:        borderHi,
      colorShadow:        '#000000',
      colorModalBackdrop: isDark ? '#000000' : '#09090b',
    },
    elements: {
      // ── Card ──────────────────────────────────────────────────────────────
      card: {
        boxShadow: 'none',
        border: `1px solid ${borderHi}`,
        borderRadius: '1rem',
      },

      // ── Social / OAuth buttons ─────────────────────────────────────────
      socialButtonsBlockButton: {
        border: `1px solid ${borderHi}`,
        boxShadow: 'none',
        borderRadius: '0.625rem',
        '&:hover': { backgroundColor: hover },
      },

      // ── Form inputs ────────────────────────────────────────────────────
      formFieldInput: {
        borderRadius: '0.625rem',
        boxShadow: 'none',
        '&:focus': {
          borderColor: ACCENT,
          boxShadow: `0 0 0 3px ${ACCENT}26`,
        },
      },

      // ── Buttons ────────────────────────────────────────────────────────
      formButtonPrimary: {
        boxShadow: 'none',
        letterSpacing: '-0.01em',
        '&:hover:not(:disabled)': { backgroundColor: ACCENT_HOVER },
        '&:active':               { backgroundColor: ACCENT_HOVER },
      },
      formButtonReset: {
        '&:hover': { backgroundColor: hover, color: textPrimary },
      },

      // ── Links ──────────────────────────────────────────────────────────
      footerActionLink: {
        fontWeight: '500',
        '&:hover': { color: ACCENT_HOVER },
      },
      identityPreviewEditButton: {
        color: ACCENT,
      },

      // ── Divider ────────────────────────────────────────────────────────
      dividerLine: {
        backgroundColor: border,
      },

      // ── Footer ─────────────────────────────────────────────────────────
      footer: {
        borderTop: `1px solid ${border}`,
      },

      // ── Header ─────────────────────────────────────────────────────────
      headerTitle: {
        fontWeight: '700',
        letterSpacing: '-0.02em',
      },

      // ── UserButton popover ──────────────────────────────────────────────
      userButtonPopoverCard: {
        boxShadow: isDark
          ? '0 8px 32px rgba(0,0,0,0.7)'
          : '0 8px 32px rgba(0,0,0,0.1)',
        border: `1px solid ${borderHi}`,
        borderRadius: '1rem',
      },
      userButtonPopoverActionButton: {
        borderRadius: '0.625rem',
        '&:hover': { backgroundColor: hover },
      },
      userButtonPopoverFooter: {
        borderTop: `1px solid ${border}`,
      },

      // ── OrganizationSwitcher ────────────────────────────────────────────
      // Trigger: icon-only to fit w-20 sidebar
      organizationSwitcherTrigger: {
        padding: '4px',
        width: '40px',
        height: '40px',
        borderRadius: '0.75rem',
        '&:hover': { backgroundColor: hover },
        '&:focus': { boxShadow: 'none', outline: 'none' },
      },
      // Mute role label ("Admin", "Member") — secondary vs org name
      organizationPreviewSecondaryIdentifier: { color: textSecond },
      organizationSwitcherPopoverCard: {
        boxShadow: isDark
          ? '0 8px 32px rgba(0,0,0,0.7)'
          : '0 8px 32px rgba(0,0,0,0.1)',
        border: `1px solid ${borderHi}`,
        borderRadius: '1rem',
      },
      organizationSwitcherPopoverActionButton: {
        borderRadius: '0.625rem',
        '&:hover': { backgroundColor: hover },
      },
      organizationSwitcherPopoverFooter: {
        borderTop: `1px solid ${border}`,
      },
    },
    captcha: { theme: (isDark ? 'dark' : 'light') as 'dark' | 'light' },
  };
}

export const clerkLight = makeAppearance(false);
export const clerkDark  = makeAppearance(true);
